import { Permission } from "Constants";
import { useGetWhoamiQuery } from "Services";
import { UserPermissions, WhoamiGroup } from "Types";
import { useCurrentOrganization, useGroup } from "./index";

/**
 * True if the user holds any of the required permissions in a scope that applies
 * here: org-wide permissions count everywhere, group-scoped permissions count
 * only within the relevant group. Mirrors the backend's
 * `check_user_has_permissions` (org_or_admin OR group) check.
 */
export const userHasPermission = (
  permissions: Permission[],
  userPermissions: UserPermissions,
  groupId: string | undefined,
): boolean => {
  if (
    permissions.some((permission) => userPermissions.org.includes(permission))
  ) {
    return true;
  }

  if (!groupId) return false;

  const groupPermissions = userPermissions.groups[groupId];
  return (
    !!groupPermissions &&
    permissions.some((permission) => groupPermissions.includes(permission))
  );
};

type UseAllowedToProps = {
  permissions: Permission[];
  groupId?: string;
};

const useAllowedTo = ({ permissions, groupId }: UseAllowedToProps): boolean => {
  const { data: whoami } = useGetWhoamiQuery();
  const currentOrganization = useCurrentOrganization();
  const currentGroup = useGroup();

  if (!currentOrganization || !whoami) return false;

  const checkGroup: WhoamiGroup | null = groupId
    ? (whoami.org_groups.find((item) => item.group_id === groupId) ?? null)
    : currentGroup;

  return userHasPermission(
    permissions,
    whoami.permissions,
    checkGroup?.group_id,
  );
};

export default useAllowedTo;
